import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { NEWS_CATEGORY_MAP } from './newsConstants';
import './NewsBanner.css';

/** localStorage key holding the id of the last banner the user dismissed. */
const DISMISS_KEY = 'news_banner_dismissed';

/**
 * Home-page banner surfacing the latest published post that is pinned to the
 * banner (``show_in_banner``). It is dismissible per post — once closed, the
 * post's id is stored in localStorage so it stays hidden until a newer banner
 * post is published. Clicking opens the post (external link or detail page).
 *
 * @returns {JSX.Element|null} The banner, or null when there is nothing to show.
 */
export default function NewsBanner() {
  const { t } = useTranslation();
  const [post, setPost] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/news/banner`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const banner = data?.post;
        if (cancelled || !banner) return;
        setPost(banner);
        try {
          setDismissed(localStorage.getItem(DISMISS_KEY) === String(banner.id));
        } catch {
          setDismissed(false);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!post || dismissed) return null;

  const cat = NEWS_CATEGORY_MAP[post.category];

  /** Open the banner's post (external link in a new tab, else detail page). */
  const open = () => {
    if (post.post_type === 'external' && post.external_url) {
      window.open(post.external_url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(`/news/${post.slug}`);
    }
  };

  /** Dismiss the banner and remember the post id so it stays hidden. */
  const dismiss = (e) => {
    e.stopPropagation();
    try {
      localStorage.setItem(DISMISS_KEY, String(post.id));
    } catch { /* ignore — dismissal just won't persist */ }
    setDismissed(true);
  };

  return (
    <div
      className="news-banner"
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter') open(); }}
    >
      <span className="news-banner-icon">{cat?.emoji || '📰'}</span>
      <div className="news-banner-text">
        <strong className="news-banner-title">{post.title}</strong>
        {post.excerpt && <span className="news-banner-excerpt">{post.excerpt}</span>}
      </div>
      <span className="news-banner-arrow">→</span>
      <button
        type="button"
        className="news-banner-close"
        onClick={dismiss}
        aria-label={t('profile.close')}
        title={t('profile.close')}
      >
        ✕
      </button>
    </div>
  );
}
